import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Check, Pickaxe, Trash2, X } from 'lucide-react';

// Homepage FAQ growth: mined suggestions (approve/dismiss) + live extras list.
export default function FaqManager() {
  const [suggestions, setSuggestions] = useState([]);
  const [extras, setExtras] = useState([]);
  const [townExtras, setTownExtras] = useState([]);
  const [edits, setEdits] = useState({});
  const [mining, setMining] = useState(false);
  const [busy, setBusy] = useState(null);

  function load() {
    api.get('/admin/faqs/suggestions').then((r) => setSuggestions(r.data.items)).catch(() => {});
    api.get('/site-settings/faq_extras').then((r) => setExtras(r.data?.value?.items || [])).catch(() => {});
    api.get('/site-settings/town_faq_extras').then((r) => setTownExtras(r.data?.value?.items || [])).catch(() => {});
  }
  useEffect(load, []);

  async function mine() {
    setMining(true);
    try {
      await api.post('/admin/faqs/mine');
      setTimeout(() => { load(); setMining(false); }, 25000);
    } catch { setMining(false); }
  }
  async function approve(s) {
    setBusy(s.id);
    const e = edits[s.id] || {};
    try {
      await api.post(`/admin/faqs/suggestions/${s.id}/approve`, { question: e.q ?? s.question, answer: e.a ?? s.answer });
      load();
    } finally { setBusy(null); }
  }
  async function dismiss(s) {
    setBusy(s.id);
    try { await api.post(`/admin/faqs/suggestions/${s.id}/dismiss`); load(); } finally { setBusy(null); }
  }
  async function removeExtra(i) {
    if (!window.confirm('Remove this FAQ from the homepage?')) return;
    await api.delete(`/admin/faqs/extras/${i}`);
    load();
  }

  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:col-span-2" data-testid="faq-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="label text-heritage">Homepage FAQs</div>
          <p className="text-xs text-charcoal/55 mt-1 max-w-xl">
            The system mines Lester's chat questions and site searches for recurring questions the FAQ doesn't answer yet.
            Nothing goes live without your approval. Approved FAQs appear on the homepage immediately; the Google-facing
            schema picks them up at the next deploy.
          </p>
        </div>
        <button onClick={mine} disabled={mining} data-testid="faq-mine-btn"
          className="inline-flex items-center gap-2 border-2 border-heritage text-heritage px-4 py-2 rounded-full text-sm font-semibold hover:bg-heritage/5 disabled:opacity-60">
          <Pickaxe size={13}/> {mining ? 'Mining… (~20s)' : 'Mine visitor questions now'}
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-3 mt-4">
          {suggestions.map((s) => (
            <div key={s.id} className="bg-admin border border-mustard/40 rounded-xl p-4" data-testid={`faq-suggestion-${s.id}`}>
              {s.town && <span className="inline-block text-[10px] uppercase tracking-wider bg-heritage text-cream px-2 py-0.5 rounded mb-2" data-testid={`faq-town-tag-${s.id}`}>{s.town} page</span>}
              <input
                value={(edits[s.id]?.q) ?? s.question}                onChange={(e) => setEdits({ ...edits, [s.id]: { ...(edits[s.id] || {}), q: e.target.value } })}
                className="w-full bg-white border border-charcoal/10 rounded-lg px-3 py-2 text-sm font-semibold mb-2"
                data-testid={`faq-suggestion-q-${s.id}`} />
              <textarea rows={3}
                value={(edits[s.id]?.a) ?? s.answer}
                onChange={(e) => setEdits({ ...edits, [s.id]: { ...(edits[s.id] || {}), a: e.target.value } })}
                className="w-full bg-white border border-charcoal/10 rounded-lg px-3 py-2 text-sm"
                data-testid={`faq-suggestion-a-${s.id}`} />
              {s.evidence && <div className="text-[11px] text-charcoal/50 mt-1">Why: {s.evidence}</div>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => approve(s)} disabled={busy === s.id} data-testid={`faq-approve-${s.id}`}
                  className="bg-forest text-base px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                  <Check size={12}/> Approve & publish
                </button>
                <button onClick={() => dismiss(s)} disabled={busy === s.id} data-testid={`faq-dismiss-${s.id}`}
                  className="border border-charcoal/20 px-4 py-1.5 rounded-full text-xs inline-flex items-center gap-1 disabled:opacity-60">
                  <X size={12}/> Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {suggestions.length === 0 && (
        <div className="text-sm text-charcoal/45 mt-3">No suggestions waiting. They arrive automatically on the 1st of each month, or mine now.</div>
      )}

      {extras.length > 0 && (
        <div className="mt-5 pt-4 border-t border-charcoal/10">
          <div className="text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-2">Live on the homepage (in addition to the 6 core FAQs)</div>
          {extras.map((x, i) => (
            <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-charcoal/5 last:border-0" data-testid={`faq-extra-${i}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold">{x.q}</div>
                <div className="text-xs text-charcoal/60 truncate">{x.a}</div>
              </div>
              <button onClick={() => removeExtra(i)} data-testid={`faq-extra-remove-${i}`} title="Remove"
                className="text-charcoal/40 hover:text-heritage shrink-0"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}
      {townExtras.length > 0 && (
        <div className="mt-5 pt-4 border-t border-charcoal/10">
          <div className="text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-2">Live on town pages</div>
          {townExtras.map((x, i) => (
            <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-charcoal/5 last:border-0" data-testid={`faq-town-extra-${i}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold"><span className="text-heritage">{x.town}:</span> {x.q}</div>
                <div className="text-xs text-charcoal/60 truncate">{x.a}</div>
              </div>
              <button onClick={async () => { if (window.confirm('Remove this FAQ from the town page?')) { await api.delete(`/admin/faqs/extras/${i}?scope=town`); load(); } }}
                data-testid={`faq-town-extra-remove-${i}`} title="Remove" className="text-charcoal/40 hover:text-heritage shrink-0"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
